import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, MessageSquare, Check, CheckCheck, Paperclip, Megaphone, Mail, CalendarPlus } from 'lucide-react';
import PropTypes from 'prop-types';
import ChatService from '../services/ChatService';
import PushNotificationService from '../services/PushNotificationService';
import LoadingSpinner from './ui/LoadingSpinner';
import EmptyState from './ui/EmptyState.jsx';
import Skeleton from './ui/Skeleton.jsx';
import Avatar from './ui/Avatar.jsx';
import { useNotificationContext } from '../contexts/NotificationContext';
import { toast } from './notifications';
import { onAuthReset } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

import PageShell from './ui/PageShell';
import { buttonClasses } from './ui/Button';
import { Input } from './ui/Input.jsx';
import { haptic } from '../utils/haptics';
// Stale-While-Revalidate Cache fuer Chat-Daten (exportiert fuer ChatPage + ChatPageRoute)
export const chatCache = {
  data: null,       // { chats: [] (UI-gemappt), conversations: [] (rohe API-Daten), requests: [] }
  timestamp: 0,
  TTL: 3 * 60 * 1000, // 3 Minuten
  isFresh() { return this.data && (Date.now() - this.timestamp < this.TTL); },
  set(chats, requests, conversations) {
    this.data = { chats, requests, conversations: conversations || [] };
    this.timestamp = Date.now();
  },
  invalidate() { this.data = null; this.timestamp = 0; },
};
// Chatliste samt Vorschau gehoert dem angemeldeten Konto: nach Logout/Nutzerwechsel
// verwerfen, sonst sieht die naechste Person am geteilten Geraet sie bis zu 3 Min. lang.
onAuthReset(() => chatCache.invalidate());

// ⚡ Perf: reine Hilfsfunktion auf Modulebene. Vorher wurde sie bei jedem Render der
// Komponente neu erzeugt und floss in die (nicht memoisierte) Suchfilterung ein.
const getMessagePreview = (message) => {
  if (!message) return 'Noch keine Nachrichten';

  if (message.type === 'file') {
    return message.content.name || 'Datei';
  }
  if (message.type === 'system') {
    return message.content;
  }
  if (message.type === 'chat_request') {
    return 'Lernpartnerschafts-Anfrage';
  }

  return message.content?.length > 50
    ? `${message.content.substring(0, 50)}…`
    : message.content;
};

// ⚡ Perf: reines Präsentations-Component auf Modulebene. Vorher stand es IM Render-Body
// der ChatOverview - dadurch entstand bei jedem Render ein neuer Komponententyp, und
// React hängte die Online-Punkte in der Liste jedes Mal aus und wieder ein, statt sie
// nur zu aktualisieren.
const OnlineIndicator = ({ isOnline, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4'
  };

  if (typeof isOnline !== 'boolean') {
    return null;
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full border-2 border-surface ${isOnline ? 'bg-success' : 'bg-muted-foreground/50'
      }`} />
  );
};

OnlineIndicator.propTypes = {
  isOnline: PropTypes.bool,
  size: PropTypes.string,
};

const ChatOverview = ({
  currentUser,
  onBack,
  onSelectChat
}) => {
  const [chats, setChats] = useState(chatCache.data?.chats || []);
  const [chatRequests, setChatRequests] = useState(chatCache.data?.requests || []);
  const [showRequests, setShowRequests] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(!chatCache.data);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  // Real-Time: Neue Nachrichten via NotificationContext tracken
  const { notifications, refetch: refetchNotifications } = useNotificationContext();
  const lastNotifCountRef = useRef(notifications.length);

  // Lade Chat-Daten (mit Stale-While-Revalidate)
  const loadChatsAndRequests = useCallback(async (forceRefresh = false) => {
    try {
      // Stale-While-Revalidate: Wenn Cache frisch genug, kein API-Call
      if (!forceRefresh && chatCache.isFresh()) {
        return;
      }

      // Nur Loading-Spinner zeigen wenn kein Cache vorhanden
      if (!chatCache.data) {
        setIsLoading(true);
      }

      // 🔥 PERFORMANCE: Lade Conversations + Requests parallel statt sequentiell
      const [conversationsResponse, requestsResponse] = await Promise.all([
        ChatService.getConversations(),
        ChatService.getChatRequests(),
      ]);
      const conversations = conversationsResponse?.conversations || [];
      const requests = requestsResponse?.requests || [];
      setChatRequests(requests);

      // ✅ FIX: Erstelle Chat-Liste aus enriched API-Daten
      const activeChats = conversations
        .map(conversation => {
          // Finde den anderen User im Chat (nicht currentUser)
          const isUser1 = conversation.user1Id === currentUser.id;
          const otherUserId = isUser1 ? conversation.user2Id : conversation.user1Id;

          // ✅ OPTIMIERT (30. Okt): Backend liefert User-Daten direkt (enriched)
          const otherUserName = isUser1 ? conversation.user2Name : conversation.user1Name;
          const otherUserImage = isUser1 ? conversation.user2Image : conversation.user1Image;

          // Letzte Nachricht aus Conversation
          const lastMessage = conversation.tenant_chat_messages?.[0] || null;

          const unreadCount = conversation.unreadCount || 0;

          // Read-Receipt: Wann hat der Partner zuletzt gelesen?
          const partnerLastReadAt = isUser1 ? conversation.user2LastReadAt : conversation.user1LastReadAt;

          // Prüfe auf pending chat requests für diesen Chat
          const hasPendingRequest = requests.some(req =>
            req.senderId === otherUserId
          );

          // ✅ FIX 3: Konvertiere relative Avatar-URLs zu absoluten URLs
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5002';
          const absoluteImageUrl = otherUserImage
            ? (otherUserImage.startsWith('http') || otherUserImage.startsWith('/assets')
              ? otherUserImage  // Bereits absolute URL oder Frontend-Asset
              : `${backendUrl}${otherUserImage}`)  // Relative URL → Absolute
            : null;

          return {
            id: otherUserId, // Für UI-Anzeige (Liste-Item Key)
            chatId: conversation.id, // ✅ FIX 2: Echte Chat-ID (UUID) für API-Calls
            name: otherUserName || 'Lernpartner:in', // ✅ Direkt vom Backend
            image: absoluteImageUrl, // ✅ FIX 3: Absolute URL
            lastMessage: lastMessage ? {
              content: lastMessage.content,
              timestamp: lastMessage.createdAt,
              sender: lastMessage.senderId,
            } : null,
            unreadCount,
            partnerLastReadAt,
            hasPendingRequest,
            isOnline: conversation.isOnline,
            lastSeen: conversation.lastSeen,
          };
        })
        .sort((a, b) => {
          // Sortiere nach: Pending Requests → Ungelesene → Letzte Nachricht
          if (a.hasPendingRequest && !b.hasPendingRequest) return -1;
          if (!a.hasPendingRequest && b.hasPendingRequest) return 1;
          if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
          if (a.unreadCount === 0 && b.unreadCount > 0) return 1;

          const aTime = new Date(a.lastMessage?.timestamp || 0);
          const bTime = new Date(b.lastMessage?.timestamp || 0);
          return bTime - aTime;
        });

      setChats(activeChats);

      // Cache aktualisieren (activeChats fuer UI, conversations fuer ChatPageRoute)
      chatCache.set(activeChats, requests, conversations);
    } catch (error) {
      console.error('Fehler beim Laden der Chats:', error);
      // Fallback: Zeige zumindest Chat-Requests
      setChatRequests([]);
      setChats([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser.id]); // useCallback dependencies

  // useEffect to load data on mount
  useEffect(() => {
    loadChatsAndRequests(true);
  }, [loadChatsAndRequests]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      chatCache.invalidate();
      loadChatsAndRequests(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadChatsAndRequests]);

  // Real-Time: Unread Count erhoehen bei neuen Chat-Nachrichten
  useEffect(() => {
    if (notifications.length > lastNotifCountRef.current) {
      const newNotifs = notifications.slice(0, notifications.length - lastNotifCountRef.current);
      newNotifs.forEach(notif => {
        if (notif.type === 'CHAT_MESSAGE' && notif.data?.chatId) {
          setChats(prev => prev.map(c =>
            c.chatId === notif.data.chatId
              ? { ...c, unreadCount: (c.unreadCount || 0) + 1 }
              : c
          ));
        }
      });
    }
    lastNotifCountRef.current = notifications.length;
  }, [notifications]);

  // ✅ Accept Chat Request Handler
  const handleAcceptRequest = async (requestId) => {
    try {
      setIsLoadingRequests(true);
      await ChatService.acceptChatRequest(requestId);
      // Ergebnis gelungen: derselbe Impuls wie beim Annehmen auf der Profilkarte
      haptic?.success?.();

      // Anfrage aus der Liste entfernen
      setChatRequests(prev => prev.filter(req => req.id !== requestId));

      // Cache invalidieren und neu laden (neuer Chat wurde erstellt)
      chatCache.invalidate();
      await loadChatsAndRequests(true);
    } catch (error) {
      console.error('❌ Fehler beim Annehmen der Chat-Anfrage:', error);
      toast.error('Anfrage konnte nicht angenommen werden');
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // ✅ Decline Chat Request Handler
  const handleDeclineRequest = async (requestId) => {
    haptic?.light?.();
    try {
      setIsLoadingRequests(true);
      await ChatService.declineChatRequest(requestId);

      // Anfrage aus der Liste entfernen und Cache invalidieren
      chatCache.invalidate();
      setChatRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (error) {
      console.error('❌ Fehler beim Ablehnen der Chat-Anfrage:', error);
      toast.error('Anfrage konnte nicht abgelehnt werden');
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // Formatiere Zeit für Chat-Vorschau
  const formatChatTime = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'jetzt';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  // Passendes Icon zur Nachrichtenvorschau (statt Emoji-Präfix)
  const getMessagePreviewIcon = (message) => {
    if (!message) return null;
    if (message.type === 'file') return Paperclip;
    if (message.type === 'system') return Megaphone;
    if (message.type === 'chat_request') return Mail;
    return null;
  };

  // Chat als gelesen markieren und oeffnen
  const handleSelectChat = useCallback((chat) => {
    if (chat.chatId && chat.unreadCount > 0) {
      ChatService.markConversationAsRead(chat.chatId)
        .then((result) => {
          if (result?.remainingUnreadChatNotifications === 0) {
            PushNotificationService.clearNotifications().catch(() => { });
          }
        })
        .catch(() => { })
        .finally(() => {
          // Erst nach Backend-Update refetchen, sonst entsteht ein Race im Nav-Badge.
          refetchNotifications();
        });
      // Cache aktualisieren, damit remountete ChatOverview den richtigen Stand hat
      if (chatCache.data) {
        chatCache.data.chats = chatCache.data.chats.map(c =>
          c.chatId === chat.chatId ? { ...c, unreadCount: 0 } : c
        );
      }
      setChats(prev => prev.map(c =>
        c.chatId === chat.chatId ? { ...c, unreadCount: 0 } : c
      ));
    }
    onSelectChat(chat);
  }, [onSelectChat, refetchNotifications]);

  // "Session vorschlagen" ohne Umweg ueber den Chat (Issue #519): dieselbe Vorbelegung
  // wie aus der Chatseite. chat.id ist die User-ID der Lernpartner:in (siehe Mapping
  // der Konversationen weiter oben), genau die erwartet die Session-Anlage.
  const handleSuggestSession = useCallback((chat) => {
    if (!chat?.id) return;
    navigate('/create-session', {
      state: {
        partner: { id: chat.id, name: chat.name, avatar: chat.image },
      },
    });
  }, [navigate]);

  // Gefilterte Chats basierend auf Suchterm
  // ⚡ Perf: memoisiert auf [chats, searchTerm] - vorher lief der Filter (inkl.
  // getMessagePreview + toLowerCase pro Chat) bei jedem Render neu, also bei jedem
  // Such-Tastendruck über die komplette Chatliste.
  const filteredChats = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return chats.filter(chat =>
      !searchTerm ||
      chat.name.toLowerCase().includes(term) ||
      getMessagePreview(chat.lastMessage).toLowerCase().includes(term)
    );
  }, [chats, searchTerm]);

  return (
    // Scrollt im Fenster wie jede andere Ansicht (August 2026). Vorher stand hier
    // eine feste Resthoehe `h-[calc(100dvh - Kopf - Navleiste - 20px)]` mit einem
    // eigenen Scrollbereich darin. Das war gleich doppelt falsch: MainScreen haelt
    // unten ohnehin schon `--bottom-nav-clearance` frei, der Wert wurde hier also
    // ein zweites Mal abgezogen (heute haelt MainScreen `--page-bottom-nav` frei,
    // das auf `--bottom-nav-clearance` aufsetzt) — und ein Scroll-Container im Scroll-Container
    // bedeutet eigenes Scroll-Gedaechtnis und eigenes Momentum-Verhalten auf iOS.
    // Seitenkopf kommt aus PageShell (variant="main"): Bungee-Titel wie bei
    // Matches, Lernsessions und Logbuch — Chats ist ein Ziel der Bottom-Navigation,
    // deshalb kein Zurueck-Pfeil (der Tab-Wechsel ist der Weg zurueck). KEIN eigener
    // AuraLightBackground: MainScreen zeichnet den Hintergrund bereits (`embedded`).
    <PageShell
      variant="main"
      embedded
      title="Chats"
      meta={chats.length > 0 ? `${chats.length} ${chats.length === 1 ? 'Chat' : 'Chats'}` : undefined}
      metaInline
      actions={(
        <button
          type="button"
          onClick={() => { setSearchOpen(prev => !prev); if (searchOpen) setSearchTerm(''); }}
          aria-label={searchOpen ? 'Suche schließen' : 'Chats durchsuchen'}
          aria-pressed={searchOpen}
          // Geoeffnet in Tinte, nicht lila (Okt 2026): ein Zustand, keine Aktion.
          className={`relative flex h-11 w-11 items-center justify-center rounded-xl transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${searchOpen ? 'bg-foreground/10 text-foreground' : 'bg-surface-raised hover:bg-foreground/10 text-muted-foreground'}`}
        >
          <Search className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    >
      <div className="flex flex-col relative">
        {/* Klappbare Suchleiste */}
        {searchOpen && (
          // Ohne eigenes px-4: MainScreen polstert schon (sonst stand das Feld
          // eingerueckter als der Titel).
          <div className="pb-3">
            <Input
              type="search"
              leadingIcon={Search}
              placeholder="Chats durchsuchen"
              aria-label="Chats durchsuchen"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
        )}

      {/* Chat-Requests: neue Vernetzungsanfragen.
          Bis September 2026 als gelber Warnkasten mit gruenem Annehmen-Knopf —
          fiel in der sonst ruhigen Chatliste heraus (Beta-Test). Gelb heisst in
          der App "Achtung", eine Anfrage ist aber eine gute Nachricht: jetzt
          neutral wie die Chatzeilen, Lila nur auf "Annehmen" (CorporateDesign v2). */}
      {chatRequests.length > 0 && (
        <div className="relative z-10 border-b border-border flex-shrink-0">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="flex items-baseline gap-2 text-sm font-semibold text-foreground">
                Vernetzungsanfragen
                <span className="font-normal tabular-nums text-muted-foreground">{chatRequests.length}</span>
              </h2>
              <button
                type="button"
                onClick={() => setShowRequests(prev => !prev)}
                className="-mr-2 min-h-[44px] px-2 text-xs font-medium text-muted-foreground transition-colors duration-fast hover:text-foreground"
              >
                {showRequests ? 'Ausblenden' : 'Einblenden'}
              </button>
            </div>

            {showRequests && (
              <div className="space-y-3">
                {chatRequests.map(request => (
                  <div
                    key={request.id}
                    className="relative rounded-2xl border border-border bg-surface p-4"
                  >
                    <div className="flex items-start gap-3">
                      {/* User Avatar — Platzhalter (Initialen + Logo) kommt aus ui/Avatar */}
                      <Avatar
                        name={request.senderName || 'Lernpartner:in'}
                        src={request.senderImage}
                        size="row"
                      />

                      {/* Request Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground mb-1">
                          {request.senderName || 'Lernpartner:in'}
                        </h3>
                        <p className="mb-3 line-clamp-2 font-serif text-sm italic text-foreground-2">
                          {request.message || 'Möchte sich mit dir vernetzen'}
                        </p>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleAcceptRequest(request.id)}
                            disabled={isLoadingRequests}
                            className={buttonClasses('primary', 'sm', 'flex-1')}
                          >
                            <span>Annehmen</span>
                          </button>

                          <button
                            onClick={() => handleDeclineRequest(request.id)}
                            disabled={isLoadingRequests}
                            className={buttonClasses('secondary', 'sm', 'flex-1')}
                          >
                            <span>Ablehnen</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Loading Overlay */}
                    {isLoadingRequests && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-[inherit] bg-surface/70">
                        <LoadingSpinner size="small" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat Liste - Performance Optimized */}
      <div className="relative z-10">
        {isLoading ? (
          <div aria-busy="true" className="divide-y divide-border/50">
            <span className="sr-only">Lade Chats…</span>
            {[0, 1, 2, 3].map((i) => <Skeleton.Row key={i} />)}
          </div>
        ) : filteredChats.length === 0 ? (
          // Haupttabs zeigen Leerzustaende ohne eigenen Kasten (Okt 2026).
          <EmptyState
            icon={MessageSquare}
            size="sm"
            title={searchTerm ? undefined : 'Noch keine Chats'}
            text={searchTerm ? 'Nichts gefunden. Versuch einen anderen Namen.' : 'Schreib einer Lernpartner:in, mit der du vernetzt bist.'}
          />
        ) : (
          <div
            // Ab Tablet zwei Spalten. Die Trennlinien muessen dabei weg: Sie
            // teilen eine Liste, kein Raster — nebeneinander liefen sie quer
            // durch beide Spalten. Stattdessen bekommt jede Zeile ab md eine
            // eigene Kante (siehe unten), sonst schwebten die Chats im Nichts.
            className="divide-y divide-border/50 md:divide-y-0 md:grid md:grid-cols-2 md:gap-2"
          >
            {filteredChats.map(chat => (
              <div
                key={chat.id}
                onClick={() => handleSelectChat(chat)}
                className="flex min-h-[68px] cursor-pointer items-center gap-3 px-4 py-3 transition-colors duration-fast hover:bg-foreground/[0.03] active:bg-foreground/[0.06] md:rounded-2xl md:border md:border-border md:bg-surface"
              >
                {/* Avatar mit Online-Status - Lazy Loading */}
                <div className="relative flex-shrink-0">
                  {/* Faellt das Bild aus, uebernimmt ui/Avatar mit den Initialen —
                      kein Fallback-Karussell aus KI-Otter-Bild und Base64-Icon mehr. */}
                  <Avatar name={chat.name} src={chat.image} size="row" loading="lazy" decoding="async" />
                  <div className="absolute -bottom-0.5 -right-0.5">
                    <OnlineIndicator isOnline={chat.isOnline} />
                  </div>

                  {/* Pending Request Indikator */}
                  {chat.hasPendingRequest && (
                    <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-surface bg-warning" aria-hidden="true" />
                  )}

                  {/* Unread Count */}
                  {chat.unreadCount > 0 && !chat.hasPendingRequest && (
                    // Zaehler in Tinte (Okt 2026): Lila je ungelesener Zeile sprengte das Budget.
                    <div className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-foreground ring-2 ring-surface flex items-center justify-center">
                      <span className="text-background text-xs font-bold px-1">
                        {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                      </span>
                    </div>
                  )}
                </div>

                {/* Chat Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="truncate text-title-s font-semibold text-foreground">
                      {chat.name}
                    </h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className={`text-xs ${chat.unreadCount > 0 || chat.hasPendingRequest
                        ? 'text-foreground font-semibold'
                        : 'text-faint'
                        }`}>
                        {formatChatTime(chat.lastMessage?.timestamp)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className={`flex-1 truncate text-meta ${chat.unreadCount > 0 || chat.hasPendingRequest
                      ? 'font-medium text-foreground-2'
                      : 'text-muted-foreground'
                      }`}>
                      {chat.hasPendingRequest && (
                        <Mail className="inline h-4 w-4 mr-1 -mt-0.5" aria-hidden="true" />
                      )}
                      {chat.lastMessage?.sender === currentUser.id && (
                        <span className="mr-1 text-faint">Du:</span>
                      )}
                      {(() => {
                        const PreviewIcon = getMessagePreviewIcon(chat.lastMessage);
                        return PreviewIcon && !chat.hasPendingRequest
                          ? <PreviewIcon className="inline h-4 w-4 mr-1 -mt-0.5" aria-hidden="true" />
                          : null;
                      })()}
                      {getMessagePreview(chat.lastMessage)}
                    </p>

                    {/* Read-Receipt Haekchen */}
                    <div className="flex items-center gap-1 ml-2">
                      {chat.lastMessage?.sender === currentUser.id && (
                        chat.partnerLastReadAt && new Date(chat.lastMessage.timestamp) <= new Date(chat.partnerLastReadAt)
                          ? <CheckCheck className="h-4 w-4 text-muted-foreground" aria-label="gelesen" />
                          : <Check className="h-4 w-4 text-faint" aria-label="gesendet" />
                      )}
                    </div>
                  </div>
                </div>

                {/* "Session vorschlagen" direkt aus der Liste (Issue #519).
                    stopPropagation: Der Tipp auf die Zeile oeffnet weiter den Chat.
                    44px Tippziel wie in den uebrigen Listenzeilen (Mobile-Audit Sep 2026).
                    NICHT bei offener Anfrage: Solange die Lernpartnerschaft nicht
                    angenommen ist, fuehrt der Knopf in eine Sackgasse - die Person
                    steht noch nicht in GET /sessions/connected-partners. */}
                {!chat.hasPendingRequest && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleSuggestSession(chat); }}
                  // Neutral, auch beim Antippen (Okt 2026): In einer langen Liste
                  // waere sonst jede Zeile ein lila Akzent (CD: hoechstens drei je
                  // Bildschirm). Ort und Existenz bleiben: Der Knopf speist
                  // origin=CHAT (FF 3), nur der Hover ist neutral.
                  className="flex-shrink-0 flex items-center justify-center min-w-[44px] min-h-[44px] rounded-xl text-faint hover:bg-foreground/5 hover:text-foreground active:bg-foreground/10 transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={`Session mit ${chat.name} vorschlagen`}
                  title="Session vorschlagen"
                >
                  <CalendarPlus className="h-5 w-5" aria-hidden="true" />
                </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
    </PageShell>
  );
};

ChatOverview.propTypes = {
  currentUser: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onSelectChat: PropTypes.func.isRequired
};

export default ChatOverview;
